import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  listDailyChecklistDays,
  longDayLabel,
  type DailyChecklistDay,
} from '../lib/dailyChecklist';
import { todayInputValue } from '../lib/taskDates';
import { useDailyChecklist } from '../components/daily/useDailyChecklist';
import { DailyChecklistList } from '../components/daily/DailyChecklistList';
import {
  Alert,
  BackLink,
  Button,
  Card,
  EmptyState,
  FOCUS_RING,
  PageHeader,
  SkeletonRows,
} from '../components/ui';
import { ChecklistIcon, ChevronIcon } from '../components/icons';

const PAGE_SIZE = 30;

/**
 * Every day a list was kept, and how each one went.
 *
 * Read-only in the sense that matters: a past day is a record, so nothing here
 * adds, renames or deletes. Ticking still works, because forgetting to tick
 * something before midnight is the commonest way a day looks worse than it was,
 * and a record you cannot correct is one people stop trusting.
 *
 * Today is deliberately absent -- it is on the dashboard, and a page headed
 * "past days" that opens on the day you are still in reads as a duplicate. It is
 * excluded by sending the client's own today as the first cursor, so the server
 * still never has to know what day it is where the reader is.
 */
export function DailyWins() {
  const [params] = useSearchParams();
  const [days, setDays] = useState<DailyChecklistDay[]>([]);
  const [nextBefore, setNextBefore] = useState<string | null>(todayInputValue);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // The card's History link carries the day you were looking at, so walking back
  // three days with the arrows and then opening history does not lose your place.
  const [expanded, setExpanded] = useState<string | null>(params.get('day'));

  useEffect(() => {
    const controller = new AbortController();

    listDailyChecklistDays({ before: todayInputValue(), limit: PAGE_SIZE }, controller.signal)
      .then((data) => {
        setDays(data.days);
        setNextBefore(data.nextBefore);
        setLoadError(null);
      })
      .catch((requestError: unknown) => {
        if (controller.signal.aborted) return;
        setLoadError(
          requestError instanceof Error ? requestError.message : 'Could not load your history.',
        );
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  async function loadMore() {
    if (!nextBefore) return;
    setLoadingMore(true);
    try {
      const data = await listDailyChecklistDays({ before: nextBefore, limit: PAGE_SIZE });
      setDays((current) => [...current, ...data.days]);
      setNextBefore(data.nextBefore);
      setLoadError(null);
    } catch (moreError: unknown) {
      setLoadError(moreError instanceof Error ? moreError.message : 'Could not load more days.');
    } finally {
      setLoadingMore(false);
    }
  }

  return (
    <div>
      <PageHeader
        eyebrow={<BackLink to="/">Dashboard</BackLink>}
        title="Daily wins"
        description="Every day you kept a list, and how it went."
      />

      <div className="mt-8 max-w-2xl">
        {loadError ? (
          <div className="mb-4">
            <Alert onDismiss={() => setLoadError(null)}>{loadError}</Alert>
          </div>
        ) : null}

        {loading ? (
          <Card>
            <SkeletonRows count={5} />
          </Card>
        ) : days.length === 0 ? (
          <Card>
            <div className="p-6">
              <EmptyState icon={<ChecklistIcon className="h-6 w-6" />} title="No days yet">
                Days you list something on show up here once they are behind you.
              </EmptyState>
            </div>
          </Card>
        ) : (
          <>
            <div className="grid gap-3">
              {days.map((entry) => (
                <DayCard
                  key={entry.day}
                  entry={entry}
                  open={expanded === entry.day}
                  onToggle={() =>
                    setExpanded((current) => (current === entry.day ? null : entry.day))
                  }
                />
              ))}
            </div>

            {nextBefore ? (
              <div className="mt-4">
                <Button variant="secondary" loading={loadingMore} onClick={() => void loadMore()}>
                  Load more
                </Button>
              </div>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

/**
 * One past day.
 *
 * Its own component because it calls `useDailyChecklist`, and a hook cannot live
 * inside a `.map`. That is also what keeps it honest: a day's items are fetched
 * when it is opened, so the page costs one request until somebody looks at
 * something.
 */
function DayCard({
  entry,
  open,
  onToggle,
}: {
  entry: DailyChecklistDay;
  open: boolean;
  onToggle: () => void;
}) {
  /**
   * The score, once this day has actually been read.
   *
   * `entry` comes from the group-by that drew the page and never hears about a
   * tick made here, so without this the header sits at "1/2" directly above a
   * list where both lines are struck out. Ticking a past day is the one thing
   * this page is for, so that is the one number it cannot get wrong.
   *
   * Held here rather than derived, because the items live a level down -- the
   * hook that owns them cannot be called until the day is open, and a hook
   * cannot be called conditionally.
   */
  const [live, setLive] = useState<DailyChecklistDay | null>(null);
  const score = live ?? entry;

  return (
    <Card>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className={`flex w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-stone-50 ${FOCUS_RING}`}
      >
        <ChevronIcon
          className={`h-4 w-4 shrink-0 text-stone-400 transition-transform ${open ? '' : '-rotate-90'}`}
        />

        <span className="min-w-0 flex-1 truncate font-medium tracking-tight text-stone-800">
          {longDayLabel(entry.day)}
        </span>

        <span className="shrink-0 text-dense tabular-nums text-stone-500">
          {score.done}/{score.total}
        </span>

        {/* The card's rail, at the size a row can spare. Fixed width rather than
            flexed, so a short day and a long one are compared by fill and not by
            length. */}
        <span aria-hidden="true" className="h-1 w-16 shrink-0 overflow-hidden rounded-full bg-stone-200">
          <span
            className="block h-full rounded-full bg-emerald-500"
            style={{ width: score.total === 0 ? '0%' : `${(score.done / score.total) * 100}%` }}
          />
        </span>
      </button>

      {open ? <DayItems day={entry.day} onScore={setLive} /> : null}
    </Card>
  );
}

function DayItems({
  day,
  onScore,
}: {
  day: string;
  onScore: (score: DailyChecklistDay) => void;
}) {
  const list = useDailyChecklist(day);

  // Reported up rather than lifted: the row above needs the two numbers, not the
  // rows. Skipped while the day is in flight, so the header keeps showing the
  // group-by's answer until there is a better one rather than blinking to 0/0.
  const { items, doneCount, loading } = list;
  useEffect(() => {
    if (loading) return;
    onScore({ day, total: items.length, done: doneCount });
  }, [day, items.length, doneCount, loading, onScore]);

  if (list.loading) {
    return (
      <div className="border-t border-stone-100 px-4 py-2">
        <SkeletonRows count={3} />
      </div>
    );
  }

  if (list.loadError) {
    return (
      <div className="border-t border-stone-100 p-3">
        <Alert>{list.loadError}</Alert>
      </div>
    );
  }

  return (
    <div className="border-t border-stone-100">
      {list.actionError ? (
        <div className="border-b border-stone-100 p-3">
          <Alert onDismiss={list.dismissActionError}>{list.actionError}</Alert>
        </div>
      ) : null}

      <DailyChecklistList
        items={list.items}
        canEdit={false}
        onToggle={(item) => void list.toggle(item)}
        onRename={() => {}}
        onRemove={() => {}}
      />
    </div>
  );
}
